// What someone does on the Purchasing plan that isn't saved anywhere else: quantities typed over the suggestion and
// lines unticked. lib/planning.ts keeps them in this browser (localStorage) for KEEP_DAYS, so a reload or the "new
// version" banner loses nothing. A typed quantity holds only while the item's open PO lines are exactly the ones it was
// typed against (`supply`: each line and its quantity): once the item goes on a PO, from the plan or anywhere else, or
// something on one arrives, the plan's own suggestion is back, and it can't come back later (a new line has a new id).
// Stock that arrives without a PO (a count, a transfer) doesn't touch it. Plain functions, so server tests can check
// them (server/test/plan-work.test.ts).

import { type VendorTerms, validQty } from "../../../shared/po.ts";

export const KEEP_DAYS = 7;
const DAY_MS = 86_400_000;

/** A quantity typed over the suggestion: when (ms), and the item's open PO lines then (supplyKey()). */
export type QtyEdit = { qty: number; at: number; supply: string };
export type PlanWork = { edits: Map<string, QtyEdit>; unticked: Map<string, number> };

/** An item's open PO lines as one string: each line's id and the quantity still to come, in a fixed order. */
export const supplyKey = (incoming: readonly { lineId: string; qty: number }[]) =>
  incoming
    .map((x) => `${x.lineId}:${x.qty}`)
    .sort()
    .join(",");

const fresh = (at: unknown, now: number) => typeof at === "number" && at <= now + DAY_MS && now - at < KEEP_DAYS * DAY_MS;

/** What was saved, without anything older than KEEP_DAYS or that doesn't read as an edit (another version's, or broken). */
export function readWork(text: string | null, now: number): PlanWork {
  const work: PlanWork = { edits: new Map(), unticked: new Map() };
  let saved: { edits?: Record<string, QtyEdit>; unticked?: Record<string, number> } | null = null;
  try {
    saved = text ? JSON.parse(text) : null;
  } catch {
    return work;
  }
  if (!saved || typeof saved !== "object") return work;
  for (const [id, e] of Object.entries(saved.edits ?? {})) {
    if (e && Number.isFinite(e.qty) && e.qty >= 0 && typeof e.supply === "string" && fresh(e.at, now))
      work.edits.set(id, { qty: e.qty, at: e.at, supply: e.supply });
  }
  for (const [id, at] of Object.entries(saved.unticked ?? {})) if (fresh(at, now)) work.unticked.set(id, at);
  return work;
}

export function writeWork(work: PlanWork): string {
  return JSON.stringify({ edits: Object.fromEntries(work.edits), unticked: Object.fromEntries(work.unticked) });
}

/** The typed quantity, while the item's open PO lines are the ones it was typed against; undefined otherwise. */
export function liveQty(edit: QtyEdit | undefined, supply: string): number | undefined {
  return edit && edit.supply === supply ? edit.qty : undefined;
}

/** Item ids whose typed quantity no longer holds (their open PO lines changed), given each planned item's supplyKey(). */
export function staleEdits(edits: ReadonlyMap<string, QtyEdit>, supplyOf: (itemId: string) => string | undefined): string[] {
  const out: string[] = [];
  for (const [id, e] of edits) {
    const now = supplyOf(id);
    if (now !== undefined && liveQty(e, now) === undefined) out.push(id);
  }
  return out;
}

/**
 * The new total for an item already on the draft the plan's lines go to: what's on it plus the plan's need, rounded by
 * the vendor's MOQ and multiple once for the whole line (the suggestion is rounded as if it were an order of its own, so
 * adding it would add a second MOQ); a quantity typed over the suggestion is added as typed.
 */
export function raisedQty(lineQty: number, qty: number, plan: { qty: number; rawQty: number; terms: VendorTerms | null } | undefined): number {
  if (!plan || qty !== plan.qty) return lineQty + qty;
  return validQty(plan.terms ?? undefined, lineQty + plan.rawQty);
}
